import { api } from '../api.ts';
import { publicPage, wireChrome } from '../components/chrome.ts';
import { formData, html, mount } from '../html.ts';
import { currentIntlLocale, formatDate, ROLE, t } from '../i18n.ts';
import { brand } from '../state.ts';
import { errorMessage, field } from '../ui.ts';

interface InvitationInfo {
  organization: string;
  email: string;
  role: string;
  invited_by: string | null;
  expires_at: string;
  account_exists: boolean;
  signed_in_as: string | null;
}

/** Acceptance page for /invite/<token>; works signed in or out. */
export async function renderInvite(root: HTMLElement, token: string, onDone: () => void): Promise<void> {
  let info: InvitationInfo;
  try {
    info = await api.get<InvitationInfo>(`/invitations/${encodeURIComponent(token)}`);
  } catch (error) {
    mount(
      root,
      publicPage(html`<main class="pma-auth"><section class="p-card pma-auth__card"><div class="p-card__body p-stack" data-p-gap="3">
        <h1>${t('Einladung nicht gültig')}</h1>
        <p>${t('Der Link ist abgelaufen, wurde widerrufen oder schon verwendet. Bitte die einladende Person um einen neuen Link.')}</p>
        <p class="pma-muted pma-small">${errorMessage(error)}</p>
        <p><a href="/login">${t('Zur Anmeldung')}</a></p></div></section></main>`),
    );
    wireChrome(root);
    return;
  }

  const sameAccount = info.signed_in_as?.toLowerCase() === info.email.toLowerCase();
  const otherAccount = info.signed_in_as && !sameAccount;
  const next = `/invite/${token}`;
  mount(
    root,
    publicPage(html`<main class="pma-auth">
      <section class="pma-auth__intro">
        <img class="pma-brand-logo pma-brand-logo--lg" src="${brand.logo}" alt="" width="64" height="64">
        <h1>${t('Einladung zu {organization}', { organization: info.organization })}</h1>
        <p>${
          info.invited_by
            ? t('{name} lädt dich als {role} ein.', { name: info.invited_by, role: ROLE[info.role] ?? info.role })
            : t('Du bist als {role} eingeladen.', { role: ROLE[info.role] ?? info.role })
        }</p>
        <p class="pma-muted pma-small">${t('Für {email} · gültig bis {date}', { email: info.email, date: formatDate(info.expires_at) })}</p>
      </section>
      <section class="p-card pma-auth__card"><div class="p-card__body p-stack" data-p-gap="4">
        ${
          sameAccount
            ? html`<h2>${t('Einladung annehmen')}</h2>
                <p>${t('Du bist als {email} angemeldet.', { email: info.email })}</p>
                <button class="p-button p-button--primary" type="button" data-accept>${t('Beitreten')}</button>`
            : otherAccount
              ? html`<h2>${t('Anderes Konto angemeldet')}</h2>
                  <p>${t('Du bist als {current} angemeldet, die Einladung gilt aber für {email}.', { current: info.signed_in_as, email: info.email })}</p>
                  <button class="p-button" type="button" data-signout>${t('Abmelden und fortfahren')}</button>`
              : info.account_exists
                ? html`<h2>${t('Mit deinem Konto anmelden')}</h2>
                    <p>${t('Für {email} gibt es schon ein Konto. Melde dich an, danach kannst du beitreten.', { email: info.email })}</p>
                    <a class="p-button p-button--primary" href="/login?next=${encodeURIComponent(next)}">${t('Anmelden')}</a>`
                : html`<h2>${t('Konto anlegen und beitreten')}</h2>
                    <form class="p-stack" data-p-gap="4" novalidate data-signup>
                      <label class="p-field"><span class="p-label">${t('E-Mail')}</span><input class="p-input" value="${info.email}" readonly></label>
                      ${field(t('Dein Name'), 'name', 'text', { required: true, attrs: 'autocomplete="name"' })}
                      ${field(t('Passwort'), 'password', 'password', { required: true, hint: t('Mindestens 10 Zeichen'), attrs: 'minlength="10" autocomplete="new-password"' })}
                      <button class="p-button p-button--primary" type="submit">${t('Konto anlegen und beitreten')}</button>
                    </form>`
        }
        <div class="p-alert p-alert--danger" role="alert" hidden data-error><span class="p-alert__icon" aria-hidden="true">!</span><div data-error-text></div></div>
      </div></section>
    </main>`),
  );
  wireChrome(root);

  const errorBox = root.querySelector<HTMLElement>('[data-error]')!;
  const accept = async (body: Record<string, unknown>, button: HTMLButtonElement) => {
    button.disabled = true;
    errorBox.hidden = true;
    try {
      await api.post(`/invitations/${encodeURIComponent(token)}/accept`, body);
      onDone();
    } catch (error) {
      errorBox.querySelector('[data-error-text]')!.textContent = errorMessage(error);
      errorBox.hidden = false;
      button.disabled = false;
    }
  };
  root.querySelector<HTMLButtonElement>('[data-accept]')?.addEventListener('click', (event) => void accept({}, event.currentTarget as HTMLButtonElement));
  root.querySelector<HTMLButtonElement>('[data-signout]')?.addEventListener('click', async () => {
    await api.post('/auth/logout').catch(() => {});
    await renderInvite(root, token, onDone);
  });
  const form = root.querySelector<HTMLFormElement>('[data-signup]');
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const data = formData(form);
    void accept(
      { name: data.name, password: data.password, locale: currentIntlLocale().startsWith('en') ? 'en' : 'de' },
      form.querySelector<HTMLButtonElement>('button[type="submit"]')!,
    );
  });
}
